import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Building2, CheckCircle2, ShieldCheck, FileText, ChevronRight, Home } from 'lucide-react';
import { MobileLayout } from '../../components/MobileLayout';
import { useScreenInit } from '../../useScreenInit.js';
import { getReferenteById, referenteFullName, TYPE_STYLE } from './referentiData';
import { APARTMENTS, TYPE_STYLE as APT_TYPE_STYLE } from './appartamentiData';
export function CmReferenteDettaglioPage() {
  useScreenInit();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const referente = getReferenteById(id);
  if (!referente) {
    return <MobileLayout data-id="element-2440">
        <div className="text-center py-20" data-id="element-2441">
          <p className="text-sm text-muted-foreground mb-4" data-id="element-2442">
            Referente non trovato.
          </p>
          <Link to="/cm/clienti/referenti" className="inline-flex items-center gap-1.5 text-sm font-medium text-coral hover:underline" data-id="element-2443">
            <ArrowLeft size={15} data-id="element-2444" />
            Torna ai referenti
          </Link>
        </div>
      </MobileLayout>;
  }
  const fullName = referenteFullName(referente);
  // Associated apartments: matched by the apartment's "referente" field.
  const apartments = APARTMENTS.filter(a => a.referente === fullName);
  const activeContracts = apartments.filter(a => a.contract?.active).length;
  return <MobileLayout data-id="element-2445">
      {/* Back */}
      <Link to="/cm/clienti/referenti" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-navy transition-colors mb-4" data-id="element-2446">
        <ArrowLeft size={16} data-id="element-2447" />
        Referenti
      </Link>

      {/* Header card */}
      <div className="bg-white border border-border rounded-xl shadow-sm p-4 lg:p-5 mb-6" data-id="element-2448">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4" data-id="element-2449">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-navy font-bold flex-shrink-0 text-xl" data-id="element-2450">
            {referente.firstName[0]}
            {referente.lastName[0]}
          </div>
          <div className="flex-1 min-w-0" data-id="element-2451">
            <div className="flex items-center gap-2 flex-wrap mb-1" data-id="element-2452">
              <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-2453">
                {fullName}
              </h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${TYPE_STYLE[referente.type]}`} data-id="element-2454">
                {referente.type}
              </span>
              {referente.hasVikey && <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-navy/5 text-navy border border-navy/20" data-id="element-2455">
                  <ShieldCheck size={12} data-id="element-2456" />
                  Vikey
                </span>}
            </div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-id="element-2457">
              <MapPin size={14} className="flex-shrink-0" data-id="element-2458" />
              {referente.zone}
            </div>
            {/* Contacts */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3" data-id="element-2459">
              <a href={`mailto:${referente.email}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-navy transition-colors min-w-0" data-id="element-2460">
                <Mail size={15} className="text-gray-400 flex-shrink-0" data-id="element-2461" />
                <span className="truncate" data-id="element-2462">{referente.email}</span>
              </a>
              <a href={`tel:${referente.phone}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-navy transition-colors" data-id="element-2463">
                <Phone size={15} className="text-gray-400 flex-shrink-0" data-id="element-2464" />
                {referente.phone}
              </a>
            </div>
          </div>
          <div className="flex-shrink-0" data-id="element-2465">
            {referente.activeContract ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-2466">
                <CheckCircle2 size={13} data-id="element-2467" />
                Contratto attivo
              </span> : <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200" data-id="element-2468">
                Nessun contratto
              </span>}
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3 mb-6" data-id="element-2469">
        <div className="bg-white border border-border rounded-xl p-4" data-id="element-2470">
          <div className="w-9 h-9 rounded-lg bg-navy/10 text-navy flex items-center justify-center mb-3" data-id="element-2471">
            <Building2 size={18} data-id="element-2472" />
          </div>
          <div className="text-2xl font-bold text-navy leading-tight" data-id="element-2473">
            {apartments.length}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5" data-id="element-2474">
            Appartamenti associati
          </div>
        </div>
        <div className="bg-white border border-border rounded-xl p-4" data-id="element-2475">
          <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3" data-id="element-2476">
            <FileText size={18} data-id="element-2477" />
          </div>
          <div className="text-2xl font-bold text-navy leading-tight" data-id="element-2478">
            {activeContracts}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5" data-id="element-2479">
            Contratti attivi
          </div>
        </div>
      </div>

      {/* Associated apartments */}
      <div className="flex items-center justify-between mb-3" data-id="element-2480">
        <h2 className="text-sm font-semibold text-navy flex items-center gap-2" data-id="element-2481">
          <Home size={16} className="text-coral" data-id="element-2482" />
          Appartamenti associati
        </h2>
        <span className="text-xs text-muted-foreground" data-id="element-2483">
          {apartments.length}{' '}
          {apartments.length === 1 ? 'immobile' : 'immobili'}
        </span>
      </div>

      {apartments.length > 0 ? <div className="space-y-3" data-id="element-2484">
          {apartments.map(a => {
        const remaining = a.cleaningsTotal - a.cleaningsDone;
        return <div key={a.id} role="button" tabIndex={0} onClick={() => navigate(`/cm/clienti/appartamenti/${a.id}`)} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate(`/cm/clienti/appartamenti/${a.id}`);
          }
        }} aria-label={`Apri appartamento ${a.name}`} className="w-full cursor-pointer text-left bg-white border border-border rounded-xl shadow-sm p-3 flex items-center gap-3 hover:border-coral/50 hover:shadow transition-all focus:outline-none focus:ring-2 focus:ring-coral" data-id="element-2485">
                <img src={a.cover} alt={a.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0 border border-border bg-gray-100" data-id="element-2486" />
                <div className="flex-1 min-w-0" data-id="element-2487">
                  <div className="flex items-center gap-2 flex-wrap" data-id="element-2488">
                    <span className="text-sm font-semibold text-navy truncate" data-id="element-2489">
                      {a.name}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border whitespace-nowrap ${APT_TYPE_STYLE[a.type]}`} data-id="element-2490">
                      {a.type}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5" data-id="element-2491">
                    <MapPin size={11} className="text-gray-400 flex-shrink-0" data-id="element-2492" />
                    <span className="truncate" data-id="element-2493">{a.address}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-[11px]" data-id="element-2494">
                    {a.contract?.active ? <button type="button" onClick={e => {
                e.stopPropagation();
                navigate(`/cm/clienti/appartamenti/${a.id}/contratto`);
              }} aria-label={`Apri dettaglio contratto di ${a.name}`} className="inline-flex items-center gap-1 text-emerald-600 font-medium hover:text-emerald-700 hover:underline focus:outline-none focus:ring-2 focus:ring-emerald-300 rounded" data-id="element-2495">
                        <CheckCircle2 size={11} data-id="element-2496" />
                        Contratto attivo
                        <ChevronRight size={11} data-id="element-2497" />
                      </button> : <span className="text-gray-400" data-id="element-2498">Nessun contratto</span>}
                    {a.cleaningsTotal > 0 && <span className="text-gray-500" data-id="element-2499">
                        {a.cleaningsDone}/{a.cleaningsTotal} pulizie ·{' '}
                        {remaining} rimaste
                      </span>}
                  </div>
                </div>
                <ChevronRight size={18} className="text-gray-300 flex-shrink-0" data-id="element-2500" />
              </div>;
      })}
        </div> : <div className="bg-white border border-border rounded-xl p-10 text-center" data-id="element-2501">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3" data-id="element-2502">
            <Building2 size={20} className="text-muted-foreground" data-id="element-2503" />
          </div>
          <p className="text-sm font-medium text-foreground" data-id="element-2504">
            Nessun appartamento associato
          </p>
          <p className="text-xs text-muted-foreground mt-1" data-id="element-2505">
            Gli immobili gestiti da questo referente compariranno qui.
          </p>
        </div>}
    </MobileLayout>;
}